import { useEffect, useState } from 'react';
import { PhinAvatarLazy } from './PhinAvatarLazy';
import css from './PhinNotificationBadge.module.css';
import { useIsTourActive } from './tour-state';

const PHIN_DURATION_MS = 5_000;
const COUNT_DURATION_MS = 60_000 - PHIN_DURATION_MS;

type PhinNotificationBadgeProps = {
  count: number;
  isHovered?: boolean;
};

export function PhinNotificationBadge({
  count,
  isHovered = false,
}: PhinNotificationBadgeProps) {
  const [hasShownPhin, setHasShownPhin] = useState(false);
  const [showPhin, setShowPhin] = useState(false);
  const isTourActive = useIsTourActive();

  useEffect(() => {
    if (isTourActive) {
      setShowPhin(false);
      return undefined;
    }

    const timeout = setTimeout(
      () => setShowPhin((shown) => !shown),
      showPhin ? PHIN_DURATION_MS : COUNT_DURATION_MS,
    );

    return () => clearTimeout(timeout);
  }, [showPhin, isTourActive]);

  const isPhinVisible = (showPhin || isHovered) && !isTourActive;

  if (isPhinVisible && !hasShownPhin) {
    setHasShownPhin(true);
  }

  if (count <= 0) {
    return null;
  }

  return (
    <span className={`${css.badge} ${isPhinVisible ? css.expanded : ''}`}>
      <span className={css.srOnly}>{count}</span>
      <span
        className={`${css.layer} ${isPhinVisible ? css.hidden : css.visible}`}
        aria-hidden="true"
      >
        <span className={css.count}>{count}</span>
      </span>
      <span
        className={`${css.layer} ${isPhinVisible ? css.visible : css.hidden}`}
        aria-hidden="true"
      >
        <span className={css.avatar}>
          {hasShownPhin && (
            <PhinAvatarLazy
              size={33}
              interactive={false}
              idleJumps={false}
              paused={!isPhinVisible}
            />
          )}
        </span>
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          className={`${css.bell} ${isPhinVisible ? css.ringing : ''}`}
        >
          <path d="M12 2a1.5 1.5 0 0 0-1.5 1.5v.6A6.5 6.5 0 0 0 5.5 10.5v4.2l-1.7 2.6A1 1 0 0 0 4.6 19h14.8a1 1 0 0 0 .8-1.7l-1.7-2.6v-4.2a6.5 6.5 0 0 0-5-6.4v-.6A1.5 1.5 0 0 0 12 2Zm-2.5 18a2.5 2.5 0 0 0 5 0h-5Z" />
        </svg>
      </span>
    </span>
  );
}

export function NotificationCount({ count }: { count: number }) {
  return <span className={css.count}>{count}</span>;
}
